/**
 * 物品 / 器物编辑器（营造司·什物谱）：
 * 在这个面板里增删改 ITEMS 与 PROP_DEFS 两张表，
 * 改完立刻生效——布置面板里能选、地图上能放、背包里能看见。
 * 定义存在 localStorage，也能导出成 JSON 或可直接贴回代码的 JS 片段。
 */
import { ITEMS, PROP_DEFS, SEAT_DEFS, getItem,
  PROP_SHAPES, PROP_ACTIONS,
  registerItem, registerProp, registerSeat, removeSeat, removeDef, isBuiltin,
  exportCustomDefs, loadDefs, saveDefsToStorage } from '../data/items.js';
import { EV } from '../core/eventbus.js';
import { downloadText, pickText } from './download.js';

export class ItemForge {
  constructor(engine, { toast } = {}) {
    this.engine = engine;
    this.toast = toast || ((text, kind) => engine.events.emit(EV.TOAST, { text, kind }));
    this.kind = 'item';
    this.sel = null;
    this.panel = document.getElementById('forge');
    this.listEl = document.getElementById('forge-list');
    this.tipEl = document.getElementById('forge-tip');
    this.$ = (id) => document.getElementById(id);
    this._fillSelects();
    this._bind();
    this._renderList();
    this._renderForm();
  }

  _fillSelects() {
    const shape = this.$('forge-shape');
    if (shape) shape.innerHTML = PROP_SHAPES.map((s) => `<option value="${s.id}">${s.name}</option>`).join('');
    const act = this.$('forge-action');
    if (act) act.innerHTML = PROP_ACTIONS.map((a) => `<option value="${a.id}">${a.name}</option>`).join('');
  }

  /* ---------------------------------- 开关 --------------------------------- */

  toggle(force) {
    const on = force === undefined ? !this._open : !!force;
    this._open = on;
    if (this.panel) this.panel.classList.toggle('hidden', !on);
    if (on) { this._renderList(); this._renderForm(); }
  }

  isOpen() { return !!this._open; }

  close() { this.toggle(false); }

  _bind() {
    const on = (id, ev, fn) => { const el = this.$(id); if (el) el.addEventListener(ev, fn); };
    on('forge-tab-item', 'click', () => this._switch('item'));
    on('forge-tab-prop', 'click', () => this._switch('prop'));
    on('forge-new', 'click', () => this._newDef());
    on('forge-del', 'click', () => this._delDef());
    on('forge-save', 'click', () => this._save());
    on('forge-export', 'click', () => this._exportJson());
    on('forge-js', 'click', () => this._exportCode());
    on('forge-import', 'click', () => this._import());
    on('forge-done', 'click', () => this.close());
    const close = this.panel && this.panel.querySelector('[data-close]');
    if (close) close.onclick = () => this.close();

    // 表单改动即时入表，边填边能在世界里看见
    for (const id of ['forge-name', 'forge-desc', 'forge-stack', 'forge-w', 'forge-h',
      'forge-solid', 'forge-layer', 'forge-shape', 'forge-action', 'forge-label',
      'forge-hint', 'forge-cost', 'forge-state', 'forge-seatable', 'forge-pose',
      'forge-ox', 'forge-oy']) {
      const el = this.$(id);
      if (!el) continue;
      el.addEventListener('change', () => this._save(true));
    }
  }

  _switch(kind) {
    this.kind = kind;
    this.$(`forge-tab-${kind}`).classList.add('on');
    this.$(`forge-tab-${kind === 'item' ? 'prop' : 'item'}`).classList.remove('on');
    this.sel = null;
    this._renderList();
    this._renderForm();
  }

  /* --------------------------------- 列表 --------------------------------- */

  _table() { return this.kind === 'item' ? ITEMS : PROP_DEFS; }

  _renderList() {
    if (!this.listEl) return;
    const table = this._table();
    this.listEl.innerHTML = '';
    for (const [id, def] of Object.entries(table)) {
      const b = document.createElement('button');
      b.className = 'forge-item' + (id === this.sel ? ' on' : '');
      if (!isBuiltin(this.kind, id)) b.classList.add('custom');
      b._id = id;
      b.innerHTML = `<b>${def.name || id}</b><i>${id}</i>`;
      b.onclick = () => { this.sel = id; this._renderList(); this._renderForm(); };
      this.listEl.appendChild(b);
    }
    if (!this.sel) {
      const first = Object.keys(table)[0];
      if (first) this.sel = first;
    }
  }

  _renderForm() {
    const isItem = this.kind === 'item';
    const off = (name, show) => {
      for (const el of document.querySelectorAll(`[data-for="${name}"]`)) {
        el.style.display = show ? '' : 'none';
      }
    };
    for (const n of ['item', 'prop', 'seat']) off(n, false);
    off(isItem ? 'item' : 'prop', true);

    const id = this.sel;
    const def = id ? this._table()[id] : null;
    const set = (elId, v) => { const el = this.$(elId); if (el) el.value = v === undefined || v === null ? '' : String(v); };
    const check = (elId, v) => { const el = this.$(elId); if (el) el.checked = !!v; };

    set('forge-id', id || '');
    if (!def) {
      set('forge-name', ''); set('forge-desc', ''); set('forge-stack', 99);
      set('forge-w', 1); set('forge-h', 1); set('forge-shape', 'box');
      set('forge-action', ''); set('forge-label', ''); set('forge-hint', '');
      set('forge-cost', '{}'); set('forge-state', '{}');
      check('forge-solid', true); check('forge-seatable', false);
      this._multiSeats = null;
      if (this.$('forge-layer')) this.$('forge-layer').value = 'furniture';
      if (this.$('forge-pose')) this.$('forge-pose').value = 'sit';
      if (this.tipEl) this.tipEl.textContent = '左边挑一件改，或点「新立」从头加。';
      return;
    }

    set('forge-name', def.name || '');
    set('forge-desc', def.desc || '');
    if (isItem) {
      set('forge-stack', def.stack || 99);
    } else {
      set('forge-w', def.w || 1);
      set('forge-h', def.h || 1);
      check('forge-solid', def.solid !== false);
      if (this.$('forge-layer')) this.$('forge-layer').value = def.layer || 'furniture';
      if (this.$('forge-shape')) this.$('forge-shape').value = def.shape || 'box';
      const act = (def.interact && def.interact.action) || '';
      if (this.$('forge-action')) this.$('forge-action').value = act;
      set('forge-label', (def.interact && def.interact.label) || '');
      set('forge-hint', (def.interact && def.interact.hint) || '');
      set('forge-cost', JSON.stringify(def.cost || {}));
      set('forge-state', JSON.stringify(def.state || {}));
      // 一桌多席（游戏桌四条凳）：只把第一条摊在表单上，其余原样带回去
      const rawSeat = SEAT_DEFS[id];
      const multi = rawSeat && Array.isArray(rawSeat.seats) ? rawSeat.seats : null;
      this._multiSeats = multi;
      const seat = multi ? multi[0] : rawSeat;
      check('forge-seatable', !!seat);
      off('seat', !!seat);
      if (seat) {
        if (this.$('forge-pose')) this.$('forge-pose').value = seat.pose || 'sit';
        set('forge-ox', seat.ox ?? 0.5);
        set('forge-oy', seat.oy ?? 0.5);
      }
    }
    if (this.tipEl) {
      this.tipEl.textContent = isBuiltin(this.kind, id)
        ? `「${def.name}」是出厂就有的，改了会一直生效（删掉也只是还原）。`
        : `「${def.name}」是后来加的。`;
    }
  }

  /* --------------------------------- 增删改 -------------------------------- */

  _newDef() {
    const id = window.prompt('代号（小写字母 / 数字 / 下划线，例如 iron_pot）', '');
    if (id === null) return;
    const clean = String(id).trim().replace(/[^A-Za-z0-9_]/g, '_');
    if (!clean) return this.toast('代号只能用字母数字与下划线。', 'warn');
    if (this._table()[clean]) return this.toast('已经有同名的了。', 'warn');
    if (this.kind === 'item') registerItem(clean, { name: clean, desc: '', stack: 99 });
    else registerProp(clean, { name: clean, w: 1, h: 1, solid: true, layer: 'furniture' });
    saveDefsToStorage();
    this.sel = clean;
    this._renderList();
    this._renderForm();
    this.toast(`新立「${clean}」，接着把右边填好吧。`, 'good');
  }

  _delDef() {
    const id = this.sel;
    if (!id) return;
    const def = this._table()[id];
    const builtin = isBuiltin(this.kind, id);
    const what = builtin ? `把「${def.name}」还原成出厂的样子？` : `删掉「${def.name}」？`;
    if (!window.confirm(what)) return;
    removeDef(this.kind, id);
    saveDefsToStorage();
    this.sel = null;
    this._renderList();
    this._renderForm();
    this.toast(builtin ? `「${def.name}」已还原。` : `「${def.name}」已删掉。`, 'good');
  }

  /** 把表单里的值写回数据表；silent 为真时不弹提示（边填边存用） */
  _save(silent = false) {
    const oldId = this.sel;
    const rawId = String((this.$('forge-id') || {}).value || '').trim();
    const id = rawId.replace(/[^A-Za-z0-9_]/g, '_');
    if (!id) return this.toast('代号不能空着。', 'warn');

    // 代号改了：把旧条目搬到新代号下
    if (oldId && id !== oldId) {
      if (this._table()[id]) return this.toast('这个代号已经被占了。', 'warn');
      removeDef(this.kind, oldId);
      this.sel = id;
    }

    const num = (elId, dflt) => {
      const v = Number((this.$(elId) || {}).value);
      return Number.isFinite(v) && v !== 0 ? v : dflt;
    };
    const txt = (elId) => String((this.$(elId) || {}).value || '');
    const checked = (elId) => !!(this.$(elId) || {}).checked;

    if (this.kind === 'item') {
      registerItem(id, {
        name: txt('forge-name') || id,
        desc: txt('forge-desc'),
        stack: Math.max(1, Math.min(999, num('forge-stack', 99))),
      });
    } else {
      const action = txt('forge-action');
      const def = {
        name: txt('forge-name') || id,
        w: Math.max(1, Math.min(6, num('forge-w', 1))),
        h: Math.max(1, Math.min(6, num('forge-h', 1))),
        solid: checked('forge-solid'),
        layer: (this.$('forge-layer') || {}).value === 'floor' ? 'floor' : 'furniture',
        shape: txt('forge-shape') || undefined,
        desc: txt('forge-desc'),
        cost: this._json('forge-cost', {}),
        state: this._json('forge-state', null),
      };
      if (action) {
        def.interact = {
          action,
          label: txt('forge-label') || def.name,
          hint: txt('forge-hint') || 'E 交互',
        };
      }
      registerProp(id, def);
      if (checked('forge-seatable')) {
        const one = {
          pose: (this.$('forge-pose') || {}).value === 'lie' ? 'lie' : 'sit',
          ox: Math.max(0, Math.min(2, num('forge-ox', 0.5))),
          oy: Math.max(0, Math.min(2, num('forge-oy', 0.5))),
        };
        if (this._multiSeats && this._multiSeats.length) {
          const all = this._multiSeats.slice();
          all[0] = { ...all[0], ...one };        // 头一条跟着表单改，其余保持原样
          registerSeat(id, { seats: all });
        } else {
          registerSeat(id, one);
        }
      } else {
        removeSeat(id);
      }
    }
    saveDefsToStorage();
    this._renderList();
    if (!silent) this.toast(`「${this._table()[id].name}」入谱了。`, 'good');
    else this._renderList();
  }

  _json(elId, fallback) {
    try {
      const v = JSON.parse(String((this.$(elId) || {}).value || ''));
      return v;
    } catch {
      return fallback;
    }
  }

  /* -------------------------------- 导入导出 ------------------------------- */

  _exportJson() {
    const text = JSON.stringify({ version: 1, defs: exportCustomDefs() }, null, 2);
    downloadText('dongtianju-defs.json', text);
    this.toast('什物谱已导出。', 'good');
  }

  _exportCode() {
    const d = exportCustomDefs();
    const body = `/* 由洞天居「什物谱」导出，把这段放进 src/data/items.js 末尾，\n`
      + `   或直接 import 后调用 loadDefs(CUSTOM_DEFS)。 */\n\n`
      + `export const CUSTOM_DEFS = ${JSON.stringify(d, null, 2)};\n\n`
      + `/* 用法：\n`
      + `import { loadDefs } from '../data/items.js';\n`
      + `import { CUSTOM_DEFS } from './custom_defs.js';\n`
      + `loadDefs(CUSTOM_DEFS);\n*/\n`;
    downloadText('custom_defs.js', body, 'text/javascript;charset=utf-8');
    this.toast('已导出 JS 片段，贴回代码里就成了正式的一分子。', 'good');
  }

  _import() {
    pickText('.json', (text) => {
      try {
        const lib = JSON.parse(text);
        const defs = lib.defs || lib;
        if (!defs || typeof defs !== 'object') throw new Error('不认识的谱子');
        const n = loadDefs(defs);
        saveDefsToStorage();
        this._renderList();
        this._renderForm();
        this.toast(`导入了 ${n} 条定义。`, 'good');
      } catch (err) {
        this.toast(`读不出来：${err.message}`, 'warn');
      }
    });
  }
}
